// design/OpsOrgDetail.dc.html + design/OpsImpersonate.dc.html. GET /ops/orgs/{id},
// POST /ops/orgs/{id}/suspend|unsuspend, POST /ops/users/{id}/impersonate,
// PUT /ops/orgs/{id}/notetaker (SD51: the org's "Allow the notetaker bot" switch — the same value
// the org's admins see on their settings page).
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ConfirmPrompt from "../components/ConfirmPrompt";
import OpsQuotaPanel from "./OpsQuotaPanel";
import { api, ApiFail } from "../api/client";
import type { OpsOrgDetailOut } from "../api/types";
import { formatBytes } from "../library/format";
import { Button, Notice } from "../components/ui";
import "../admin/admin.css";
import { Badge, Card, Icon, ToggleRow } from "@grackle/ui";
import { Stat } from "../components/Stat";

export default function OpsOrgDetailPage() {
  const { orgId } = useParams();
  const [org, setOrg] = useState<OpsOrgDetailOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [impersonateUrl, setImpersonateUrl] = useState<string | null>(null);
  // prompt for suspend/impersonate reason
  const [suspendPromptOpen, setSuspendPromptOpen] = useState(false);
  const [impersonateReason, setImpersonateReason] = useState<{ userId: string; label: string } | null>(null);

  const load = useCallback(() => {
    if (!orgId) return;
    api<OpsOrgDetailOut>("GET", `/ops/orgs/${orgId}`)
      .then(setOrg)
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load this org."));
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  async function onSuspendConfirm(reason: string) {
    setSuspendPromptOpen(false);
    setBusy(true);
    setActionError(null);
    try {
      await api("POST", `/ops/orgs/${orgId}/suspend`, { reason });
      load();
    } catch (err) {
      setActionError(err instanceof ApiFail ? err.message : "Couldn't suspend this org.");
    } finally {
      setBusy(false);
    }
  }

  async function onSuspend() {
    setSuspendPromptOpen(true);
  }

  async function onUnsuspend() {
    if (!orgId) return;
    setBusy(true);
    setActionError(null);
    try {
      await api("POST", `/ops/orgs/${orgId}/unsuspend`);
      load();
    } catch (err) {
      setActionError(err instanceof ApiFail ? err.message : "Couldn't unsuspend this org.");
    } finally {
      setBusy(false);
    }
  }

  async function onNotetakerBot(enabled: boolean) {
    if (!orgId) return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await api<{ notetaker_bot_enabled: boolean }>("PUT", `/ops/orgs/${orgId}/notetaker`, { enabled });
      setOrg((prev) => (prev ? { ...prev, notetaker_bot_enabled: res.notetaker_bot_enabled } : prev));
    } catch (err) {
      setActionError(err instanceof ApiFail ? err.message : "Couldn't change the notetaker bot setting.");
    } finally {
      setBusy(false);
    }
  }

  async function onImpersonateConfirm(userId: string, _label: string, reason: string) {
    setImpersonateReason(null);
    setActionError(null);
    try {
      const res = await api<{ url: string; expires_at: string }>("POST", `/ops/users/${userId}/impersonate`, {
        reason,
      });
      setImpersonateUrl(res.url);
    } catch (err) {
      setActionError(err instanceof ApiFail ? err.message : "Couldn't start that session.");
    }
  }

  async function onImpersonate(userId: string, label: string) {
    setImpersonateReason({ userId, label });
  }

  if (error) return <Notice tone="error">{error}</Notice>;
  if (!org) return <div className="gk-sm">Loading…</div>;

  return (
    <>
      <Link to="/ops/orgs" className="gk-btn gk-btn--ghost ops-back">
        <Icon name="arrow-left" size={20} />
        All orgs
      </Link>
      <header className="page-head">
        <div className="page-head__grow">
          <h1>{org.name}</h1>
          <p className="mono">{org.slug}</p>
        </div>
        {org.suspended_at ? (
          <Button variant="secondary" onClick={onUnsuspend} disabled={busy}>
            Unsuspend
          </Button>
        ) : (
          <Button variant="secondary" onClick={onSuspend} disabled={busy}>
            Suspend org
          </Button>
        )}
      </header>

      {org.suspended_at ? (
        <Notice tone="error">
          Suspended{org.suspended_reason ? `: ${org.suspended_reason}` : ""}. New uploads are blocked; existing
          recordings still play.
        </Notice>
      ) : null}
      {actionError ? <Notice tone="error">{actionError}</Notice> : null}
      {impersonateUrl ? (
        <Notice>
          Send this one-time link to whoever needs it (expires in 2 minutes):{" "}
          <span className="mono">{impersonateUrl}</span>
        </Notice>
      ) : null}

      <div className="admin-stats">
        <Stat label="Members" value={org.member_count} />
        <Stat label="Recordings" value={org.grackle_count} />
        <Stat label="Storage (approx.)" value={formatBytes(org.storage_bytes)} />
      </div>

      <Card size="flush" className="admin-table-card">
<table className="admin-table">
        <thead>
          <tr>
            <th className="gk-overline">Member</th>
            <th className="gk-overline">Role</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {org.members.map((m) => (
            <tr key={m.user_id}>
              <td>
                <div className="admin-row-name">{m.name || m.email}</div>
                <div className="admin-row-sub">{m.email}</div>
              </td>
              <td>
                <Badge size="sm">{m.role}</Badge>
              </td>
              <td>
                <div className="row-actions">
                  <Button
                    variant="secondary" size="sm"
                    onClick={() => onImpersonate(m.user_id, m.name || m.email)}
                  >
                    View as
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
</Card>

      <Card size="sm" title="Notetaker bot">
        <ToggleRow
          label="Allow the notetaker bot"
          description="When off, no one in this org can send the bot to a meeting and auto-record is skipped. The org's admins can change this too. Platform-wide switches are on the Notetaker tab."
          checked={org.notetaker_bot_enabled}
          onChange={(v) => {
            if (!busy) void onNotetakerBot(v);
          }}
        />
      </Card>

      <OpsQuotaPanel orgId={org.id} members={org.members} />

      {/* prompt for suspend reason */}
      <ConfirmPrompt
        open={suspendPromptOpen}
        title="Suspend organization"
        message="Enter a reason for suspending this org. This will be logged in the ops audit trail."
        placeholder="e.g., policy violation, billing issue…"
        onClose={() => setSuspendPromptOpen(false)}
        onConfirm={onSuspendConfirm}
      />

      {/* prompt for impersonation reason */}
      {impersonateReason && (
        <ConfirmPrompt
          open={true}
          title={`View as ${impersonateReason.label}`}
          message="Enter a reason for viewing this account. This will be recorded and a banner will be shown to the target user."
          placeholder="e.g., support ticket #456, bug investigation…"
          onClose={() => setImpersonateReason(null)}
          onConfirm={(reason) => onImpersonateConfirm(impersonateReason.userId, impersonateReason.label, reason)}
        />
      )}
    </>
  );
}
